/**
 * The shell: navy sidebar, paper content area, and the routes.
 *
 * Two things live here rather than in a screen because they are true everywhere.
 * The approvals badge is a live count from the API, so a quote waiting on
 * somebody is visible from any screen. The role switcher changes the identity
 * headers on every subsequent request, which re-fetches the whole app — a
 * different role genuinely sees different figures, and showing a cached view
 * from the previous role would be a lie about what the server would allow.
 */
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { BrowserRouter, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import {
  rememberIdentity,
  getCurrentUser,
  setCurrentUser,
  useApi,
  useCurrentUser,
  type CurrentUser,
} from './lib/api';
import { initials, roleLabel } from './lib/format';
import type { ApprovalRequest, MeResponse, Principal, Role } from './lib/types';
import { ErrorState, LoadingState } from './components';
import { ensureFreshToken, fetchIdentity, getIdToken, signOut, tokenClaims, type IdentityInfo } from './lib/signin';
import { AuthCallback, SignIn } from './screens/SignIn';

// ---------------------------------------------------------------- screens

/**
 * Screens are loaded on demand from `src/screens`. The import is resolved at
 * runtime so this file typechecks and builds before every screen exists, and a
 * screen that has not been written yet says so instead of blanking the shell.
 * Each screen module default-exports its component.
 */
function lazyScreen(name: string) {
  return lazy(async (): Promise<{ default: ComponentType }> => {
    try {
      const module = (await import(`./screens/${name}.tsx`)) as { default?: ComponentType };
      if (!module?.default) throw new Error(`${name} has no default export`);
      return { default: module.default };
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      // A tab left open across a deployment holds the old page, whose chunk
      // names no longer exist on the server, so the first screen it opens
      // after the deploy fails to import. That is a stale tab, not a missing
      // screen: reload once to pick up the new build, and only if that does
      // not cure it say so.
      if (isStaleChunk(message) && !import.meta.env.DEV) {
        const key = `pts-compass:reloaded-for:${name}`;
        if (sessionStorage.getItem(key) === null) {
          sessionStorage.setItem(key, new Date().toISOString());
          window.location.reload();
          return { default: () => <LoadingState label="A new version of Compass has been released. Reloading…" /> };
        }
      }
      return {
        default: () => <MissingScreen name={name} detail={message} stale={isStaleChunk(message)} />,
      };
    }
  });
}

/** The browser's wording when a dynamically imported chunk is gone. */
function isStaleChunk(message: string): boolean {
  return /importing a module script failed|failed to fetch dynamically imported module|loading chunk|error loading dynamically imported module/i.test(message);
}

const Overview = lazyScreen('Overview');
const Pipeline = lazyScreen('Pipeline');
const Clients = lazyScreen('Clients');
const ClientDetail = lazyScreen('ClientDetail');
const OpportunityDetail = lazyScreen('OpportunityDetail');
const QuoteWorkspace = lazyScreen('QuoteWorkspace');
const Catalogue = lazyScreen('Catalogue');
const Configuration = lazyScreen('Configuration');
const Approvals = lazyScreen('Approvals');
const NewInquiry = lazyScreen('NewInquiry');
const DocumentView = lazyScreen('DocumentView');
const ClientQuestionnaire = lazyScreen('ClientQuestionnaire');

function MissingScreen({ name, detail, stale }: { name: string; detail: string; stale: boolean }) {
  if (stale) {
    return (
      <div className="content">
        <ErrorState
          title="This tab is out of date"
          error={new Error(
            'A new version of Compass was released while this tab was open, so part of the old page can no longer be loaded. Reload to continue; nothing you saved is affected.',
          )}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }
  return (
    <div className="content">
      <ErrorState
        title={import.meta.env.DEV ? `The ${name} screen is not built yet` : `The ${name} screen could not be loaded`}
        error={new Error(
          import.meta.env.DEV
            ? `src/screens/${name}.tsx could not be loaded. It should default-export a React component. (${detail})`
            : `${detail} Reload the page; if it happens again, tell the administrator.`,
        )}
        onRetry={() => window.location.reload()}
      />
    </div>
  );
}

// ---------------------------------------------------------------- navigation

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Keeps the item active on child routes such as /clients/:id. */
  matchPrefix?: string;
  badgeKey?: 'approvals';
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Overview', icon: <IconGauge />, end: true },
  { to: '/pipeline', label: 'Pipeline', icon: <IconPipeline />, matchPrefix: '/opportunities' },
  { to: '/clients', label: 'Clients', icon: <IconClients /> },
  { to: '/quotes', label: 'Quote workspace', icon: <IconQuote /> },
  { to: '/catalogue', label: 'Catalogue', icon: <IconCatalogue /> },
  { to: '/configuration', label: 'Configuration', icon: <IconConfig /> },
  { to: '/approvals', label: 'Approvals', icon: <IconApproval />, badgeKey: 'approvals' },
];

export default function App() {
  const [identity, setIdentity] = useState<IdentityInfo | null>(null);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    // An expired ID token with a refresh token to hand is renewed here, before
    // the sign-in page is shown, so a session lasts the refresh token's life.
    fetchIdentity()
      .then(async (info) => { rememberIdentity(info); await ensureFreshToken(info); setIdentity(info); setChecked(true); })
      .catch((e: unknown) => setIdentityError(String(e)));
  }, []);
  if (identityError) return <div style={{ padding: 40 }}>The API is not reachable: {identityError}</div>;
  if (!identity || !checked) return null;
  const signedIn = identity.mode !== 'cognito' || Boolean(getIdToken());
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback identity={identity} />} />
        <Route path="*" element={signedIn ? <Shell identity={identity} /> : <SignIn identity={identity} />} />
      </Routes>
    </BrowserRouter>
  );
}

function Shell({ identity }: { identity: IdentityInfo }) {
  // The identity service is shared with another application, so a person can
  // sign in and still have no Compass role. Only the API knows which groups
  // the token carries, so ask it once before showing anything: a shell whose
  // every screen refuses the user is worse than one page that says why.
  const me = useApi<MeResponse>(identity.mode === 'cognito' ? '/me' : null);
  if (identity.mode === 'cognito') {
    if (me.error) return <NoAccess identity={identity} title="Compass could not check your account" detail={me.error.message} />;
    if (!me.data) return <div className="content"><LoadingState label="Checking your account" rows={2} /></div>;
    if (me.data.principal === null || me.data.principal.roles.length === 0) {
      return <NoAccess identity={identity} {...noAccessWording(me.data.principal, identity)} />;
    }
  }
  return <Workspace identity={identity} />;
}

/**
 * Why this account cannot use Compass, in the words the API's reason calls
 * for. The domain gate is a rule, so it is named; an absent group is just
 * something an administrator has not done yet.
 */
function noAccessWording(principal: Principal | null, identity: IdentityInfo): { title: string; detail: string } {
  const domains = (identity.allowedEmailDomains ?? []).join(', ') || 'PTS email addresses only';
  const email = String(principal?.email ?? tokenClaims()?.['email'] ?? 'not on record');
  switch (principal?.denied) {
    case 'email_domain':
      return {
        title: 'This account cannot use Compass',
        detail: `Compass is for PTS staff accounts (${domains}). This account is ${email}.`,
      };
    case 'email_unverified':
      return {
        title: 'This account cannot use Compass yet',
        detail: `The identity service has not verified the email address ${email}. Compass admits only accounts with a verified PTS email address; ask an administrator to check the account.`,
      };
    default:
      return {
        title: 'This account is not set up for Compass',
        detail: 'You are signed in, but a Compass administrator must add this account to a Compass role before it can be used here.',
      };
  }
}

/**
 * The whole of Compass for a signed-in account that holds no Compass role:
 * no navigation, no screens, no further API calls. Signing out clears the
 * token, so somebody else can sign in on the same browser.
 */
function NoAccess({ identity, title, detail }: { identity: IdentityInfo; title: string; detail: string }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0b2635', color: '#fff', padding: 24 }}>
      <div style={{ maxWidth: 420, width: '100%', background: '#fff', color: '#102430', borderRadius: 14, padding: 28 }} role="alert">
        <div style={{ fontSize: 20, fontWeight: 700 }}>{title}</div>
        <p style={{ color: '#64757e' }}>{detail}</p>
        <button type="button" className="primary" style={{ width: '100%' }} onClick={() => signOut(identity)}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function Workspace({ identity }: { identity: IdentityInfo }) {
  const location = useLocation();
  const user = useCurrentUser();
  const [menuOpen, setMenuOpen] = useState(false);

  // Pending approvals are shown on every screen, and they change when the role
  // changes: an Approver sees the queue they own.
  const approvals = useApi<ApprovalRequest[]>('/approval-requests');
  const features = useApi<{ inquiryWorkflow: boolean; inquiryPhase: string }>('/features');
  const simple = Boolean(features.data?.inquiryWorkflow && features.data.inquiryPhase === 'qualification');
  const pending = approvals.data?.length ?? 0;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="shell">
      <Sidebar
        identity={identity}
        pending={pending}
        simple={simple}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onUserChange={() => approvals.reload()}
      />
      <main>
        <Topbar
          onMenu={() => setMenuOpen((v) => !v)}
          pending={pending}
          userLabel={user.displayName ?? user.userId}
          roleName={roleLabel(user.role)}
        />
        <Suspense fallback={<div className="content"><LoadingState label="Loading the screen" rows={4} /></div>}>
          <Routes>
            <Route path="/" element={<Overview />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/inquiries/new" element={<NewInquiry />} />
            <Route path="/opportunities/:id" element={<OpportunityDetail />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:id" element={<ClientDetail />} />
            <Route path="/quotes" element={<QuoteWorkspace />} />
            <Route path="/quotes/:id" element={<QuoteWorkspace />} />
            <Route path="/quote-versions/:versionId" element={<QuoteWorkspace />} />
            <Route path="/catalogue" element={<Catalogue />} />
            <Route path="/configuration" element={<Configuration />} />
            <Route path="/configuration/:tab" element={<Configuration />} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/documents/:id" element={<DocumentView />} />
            <Route path="/questionnaires/:type" element={<ClientQuestionnaire />} />
            <Route path="/opportunities/:id/questionnaire" element={<ClientQuestionnaire />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {menuOpen && <div className="sidebar-scrim" onClick={() => setMenuOpen(false)} role="presentation" />}
    </div>
  );
}

function Sidebar({
  identity,
  pending,
  simple,
  open,
  onClose,
  onUserChange,
}: {
  pending: number;
  simple: boolean;
  open: boolean;
  onClose: () => void;
  onUserChange: () => void;
  identity: IdentityInfo;
}) {
  return (
    <aside className={open ? 'sidebar open' : 'sidebar'}>
      <div className="brand">
        <CompassMark />
        <div>
          <strong>PTS Compass</strong>
          <small>CRM &amp; quotations</small>
        </div>
      </div>

      <div className="nav-label">Workspace</div>
      <nav className="nav" aria-label="Main">
        {NAV.map((item) => (
          <NavItemLink key={item.to} item={item} pending={pending} onNavigate={onClose} />
        ))}
      </nav>

      <div className="side-bottom">
        <RoleSwitcher identity={identity} onChange={onUserChange} />
      </div>
    </aside>
  );
}

function NavItemLink({
  item,
  pending,
  onNavigate,
}: {
  item: NavItem;
  pending: number;
  onNavigate: () => void;
}) {
  const location = useLocation();
  const prefixActive =
    item.matchPrefix !== undefined && location.pathname.startsWith(item.matchPrefix);

  return (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      onClick={onNavigate}
      className={({ isActive }) => (isActive || prefixActive ? 'nav-item active' : 'nav-item')}
    >
      <span className="ico" aria-hidden="true">
        {item.icon}
      </span>
      <span className="nav-text">{item.label}</span>
      {item.badgeKey === 'approvals' && pending > 0 && (
        <span className="nav-badge" title={`${pending} awaiting a decision`}>
          {pending}
        </span>
      )}
    </NavLink>
  );
}

/**
 * The five demo users. Changing this rewrites the identity headers, and every
 * hook built on `useApi` re-fetches so the figures on screen are the ones this
 * role is actually allowed to see.
 */
function RoleSwitcher({ onChange, identity }: { onChange: () => void; identity: IdentityInfo }) {
  const current = useCurrentUser();
  const me = useApi<MeResponse>('/me');
  const users = me.data?.availableUsers ?? [];
  const canSeeCost = me.data?.canSeeCost ?? null;

  const onSelect = useCallback(
    (value: string) => {
      const [userId, role] = value.split('|');
      if (!userId || !role) return;
      const chosen = users.find((u) => u.id === userId);
      const next: CurrentUser = {
        userId,
        role: role as Role,
        ...(chosen ? { displayName: chosen.displayName } : {}),
      };
      setCurrentUser(next);
      onChange();
    },
    [users, onChange],
  );

  // One option per user per role, so Ben can act as Administrator or as Approver.
  const options = users.flatMap((user) =>
    user.roles.map((role) => ({
      value: `${user.id}|${role}`,
      label: `${user.displayName} — ${roleLabel(role)}`,
    })),
  );
  const selected = `${current.userId}|${current.role}`;
  const hasSelected = options.some((option) => option.value === selected);

  return (
    <div className="role-switcher">
      <div className="profile">
        <span className="avatar">{initials(current.displayName ?? getCurrentUser().userId)}</span>
        <div className="profile-text">
          <strong>{current.displayName ?? current.userId}</strong>
          <small>{roleLabel(current.role)}</small>
        </div>
      </div>
      {identity.mode === 'cognito' ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <div><strong>{String(tokenClaims()?.['name'] ?? tokenClaims()?.['email'] ?? 'Signed in')}</strong><br /><small>{String(tokenClaims()?.['email'] ?? '')}</small></div>
          <button type="button" className="secondary" onClick={() => signOut(identity)}>Sign out</button>
        </div>
      ) : (<>

      <label className="role-label" htmlFor="role-switcher">
        Signed in as
      </label>
      <select
        id="role-switcher"
        className="role-select"
        value={hasSelected ? selected : ''}
        onChange={(event) => onSelect(event.target.value)}
        disabled={options.length === 0}
      >
        {!hasSelected && (
          <option value="">
            {me.loading ? 'Loading users…' : `${current.userId} — ${roleLabel(current.role)}`}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select></>)}

      <p className="role-note">
        {canSeeCost === null
          ? 'Demo identity. The API re-checks every role on every request.'
          : canSeeCost
            ? 'This role sees cost, margin and gross profit.'
            : 'Cost, margin and gross profit are withheld from this role and render as an em dash.'}
      </p>
    </div>
  );
}

/** "v 1.1": the major and minor of the package version, as the MD asked for it to read. */
const APP_VERSION_LABEL = `v ${__APP_VERSION__.split('.').slice(0, 2).join('.')}`;

function Topbar({
  onMenu,
  pending,
  userLabel,
  roleName,
}: {
  onMenu: () => void;
  pending: number;
  userLabel: string;
  roleName: string;
}) {
  const location = useLocation();
  const item =
    NAV.find((n) => n.to !== '/' && location.pathname.startsWith(n.to)) ??
    NAV.find((n) => n.matchPrefix && location.pathname.startsWith(n.matchPrefix)) ??
    (location.pathname.startsWith('/quote-versions/') ? NAV.find((n) => n.to === '/quotes') : undefined) ??
    NAV[0];

  return (
    <div className="topbar">
      <div className="topbar-left">
        <button type="button" className="icon-btn mobile-menu" onClick={onMenu} aria-label="Menu">
          <IconMenu />
        </button>
        <span className="crumb">
          <span className="crumb-root">PTS Managed Services <span aria-hidden="true">/</span> </span><strong>{location.pathname === '/inquiries/new' ? 'New opportunity' : location.pathname.startsWith('/documents/') ? 'Document' : location.pathname.includes('questionnaire') ? 'Client questionnaire' : item?.label ?? 'Overview'}</strong>
        </span>
      </div>
      <div className="top-actions">
        <span className="top-version" title={`PTS Compass ${APP_VERSION_LABEL}`}>{APP_VERSION_LABEL}</span>
        {pending > 0 && (
          <NavLink to="/approvals" className="top-pending" aria-label={`${pending} awaiting approval`}>
            {pending}<span className="top-pending-text"> awaiting approval</span>
          </NavLink>
        )}
        <span className="top-identity" title={`${userLabel}, ${roleName}`}>
          <span className="avatar avatar-sm">{initials(userLabel)}</span>
          <span className="top-identity-text">{roleName}</span>
        </span>
      </div>
    </div>
  );
}

function NotFound() {
  const location = useLocation();
  return (
    <div className="content">
      <ErrorState
        title="No such screen"
        error={new Error(`Nothing is routed at ${location.pathname}.`)}
      />
    </div>
  );
}

// ---------------------------------------------------------------- marks

function CompassMark() {
  return (
    <svg className="mark" viewBox="0 0 64 64" role="img" aria-label="PTS Compass">
      <rect width="64" height="64" rx="15" fill="#0B2635" />
      <circle cx="32" cy="32" r="20" fill="none" stroke="#315261" strokeWidth="2" />
      <path d="M32 11 38.5 27.5 53 32 38.5 36.5 32 53 25.5 36.5 11 32 25.5 27.5Z" fill="#173F4F" />
      <path d="M32 14 39 34 32 31 25 34Z" fill="#2BD2BA" />
      <path d="M32 50 25 30 32 33 39 30Z" fill="#F4F7F7" />
      <circle cx="32" cy="32" r="3.5" fill="#E8B566" />
    </svg>
  );
}

function IconGauge() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 18a8 8 0 1 1 16 0" strokeLinecap="round" />
      <path d="m12 14 4-4" strokeLinecap="round" />
    </svg>
  );
}

function IconPipeline() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="4" width="5" height="16" rx="1.5" />
      <rect x="10" y="4" width="5" height="11" rx="1.5" />
      <rect x="17" y="4" width="4" height="7" rx="1.5" />
    </svg>
  );
}

function IconClients() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 20v-1.5A3.5 3.5 0 0 1 7.5 15h3A3.5 3.5 0 0 1 14 18.5V20" strokeLinecap="round" />
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M16 20v-1.5a3.5 3.5 0 0 0-2-3.16M15.5 5.6a3.2 3.2 0 0 1 0 5.8" strokeLinecap="round" />
    </svg>
  );
}

function IconQuote() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M6 3h8l4 4v14H6z" strokeLinejoin="round" />
      <path d="M14 3v4h4M9 12h6M9 16h4" strokeLinecap="round" />
    </svg>
  );
}

function IconCatalogue() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M3.5 9h17M9 9v10.5" strokeLinecap="round" />
    </svg>
  );
}

function IconConfig() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="3" />
      <path
        d="M12 3v2.2M12 18.8V21M4.6 7.8l1.9 1.1M17.5 15.1l1.9 1.1M4.6 16.2l1.9-1.1M17.5 8.9l1.9-1.1"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconApproval() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M20 6.5 10.5 16 5 10.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 12.5V19a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19V6a1.5 1.5 0 0 1 1.5-1.5h9" strokeLinecap="round" />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </svg>
  );
}
